import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SPHERE_TOOLBAR } from "./constants/sphereToolbar";
import { computeCostCurve, spheresInDeck } from "../utils/costCurve";
import { sphereAccentClasses } from "../utils/sphereAccent";

const ANIM_MS = 240;
const EASE = "cubic-bezier(0.32, 0.72, 0, 1)";

function prefersReducedMotion() {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function CostCurveChart({ buckets, total, maxCount, barsRevealed, barClass, sphereLabel }) {
  if (total === 0) {
    return (
      <p className="text-sm text-slate-500">
        No cards to chart for this selection (heroes are excluded).
      </p>
    );
  }

  const barMaxPx = 120;

  return (
    <div>
      <div
        className="flex items-end justify-center gap-1 sm:gap-2"
        role="img"
        aria-label={`Cost curve for ${sphereLabel}: ${buckets.map((b) => `cost ${b.label}, ${b.count} cards`).join("; ")}`}
      >
        {buckets.map((bucket, index) => {
          const height =
            maxCount > 0 ? Math.max(4, Math.round((bucket.count / maxCount) * barMaxPx)) : 4;
          const pct = total > 0 ? Math.round((bucket.count / total) * 100) : 0;
          return (
            <div key={String(bucket.key)} className="flex min-w-0 flex-1 flex-col items-center gap-1">
              <span
                className={[
                  "text-xs font-semibold tabular-nums text-amber-100/90 transition-opacity duration-200",
                  barsRevealed ? "opacity-100" : "opacity-0",
                ].join(" ")}
                style={{ transitionDelay: barsRevealed ? `${80 + index * 35}ms` : "0ms" }}
              >
                {bucket.count}
              </span>
              <div
                className={`w-full max-w-[2.5rem] origin-bottom rounded-t transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] sm:max-w-[3rem] ${barClass}`}
                style={{
                  height: `${height}px`,
                  transform: barsRevealed ? "scaleY(1)" : "scaleY(0)",
                  transitionDelay: barsRevealed ? `${60 + index * 35}ms` : "0ms",
                }}
                title={`${bucket.count} cards at cost ${bucket.label} (${pct}%)`}
              />
              <span className="text-xs font-medium tabular-nums text-slate-400">{bucket.label}</span>
            </div>
          );
        })}
      </div>
      <p className="mt-3 text-center text-xs text-slate-500">
        Bar height is relative to the busiest cost slot. Numbers above each bar are card counts.
      </p>
    </div>
  );
}

function CostCurveSphereFilter({ spheres, selectedSphere, onSelectSphere }) {
  if (spheres.length === 0) return null;

  const pillBase =
    "inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition";

  return (
    <div className="mt-3" role="group" aria-label="Filter cost curve by sphere">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Sphere</p>
      <div className="mt-2 flex flex-wrap gap-2">
        <button
          type="button"
          aria-pressed={selectedSphere == null}
          onClick={() => onSelectSphere(null)}
          className={[
            pillBase,
            selectedSphere == null
              ? "border-amber-500/60 bg-amber-950/70 text-amber-100 ring-2 ring-amber-500/50"
              : "border-slate-700 bg-slate-900 text-slate-400 hover:border-slate-600 hover:text-slate-200",
          ].join(" ")}
        >
          All
        </button>
        {spheres.map((sphere) => {
          const toolbar = SPHERE_TOOLBAR.find((s) => s.name === sphere.key);
          const on = selectedSphere === sphere.key;
          const accent = sphereAccentClasses(sphere.key);
          return (
            <button
              key={sphere.key}
              type="button"
              aria-pressed={on}
              onClick={() => onSelectSphere(sphere.key)}
              className={[
                pillBase,
                on
                  ? toolbar
                    ? `${toolbar.activeBg} ring-2 ${toolbar.activeRing}`
                    : `${accent.chip} ring-2 ring-slate-500/50`
                  : `border-slate-700 bg-slate-900 text-slate-400 ${toolbar?.idleHover ?? "hover:border-slate-600 hover:text-slate-200"}`,
              ].join(" ")}
            >
              {toolbar ? (
                <span className="h-4 w-4 shrink-0">{toolbar.icon("h-full w-full")}</span>
              ) : null}
              <span>{sphere.label}</span>
              <span className="tabular-nums text-[10px] opacity-80">({sphere.count})</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CostCurveTable({ buckets, total }) {
  if (total === 0) return null;

  return (
    <div className="mt-6 overflow-x-auto rounded-lg border border-slate-700">
      <table className="w-full min-w-[16rem] text-left text-sm">
        <thead>
          <tr className="border-b border-slate-700 bg-slate-900/80 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <th scope="col" className="px-3 py-2">
              Cost
            </th>
            <th scope="col" className="px-3 py-2 text-right">
              Cards
            </th>
            <th scope="col" className="px-3 py-2 text-right">
              Share
            </th>
          </tr>
        </thead>
        <tbody>
          {buckets.map((bucket) => {
            const pct = total > 0 ? (bucket.count / total) * 100 : 0;
            return (
              <tr key={String(bucket.key)} className="border-b border-slate-800/80 last:border-0">
                <td className="px-3 py-2 font-medium tabular-nums text-slate-200">{bucket.label}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-300">{bucket.count}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-400">
                  {pct < 10 && pct > 0 ? pct.toFixed(1) : Math.round(pct)}%
                </td>
              </tr>
            );
          })}
          <tr className="bg-slate-900/50 font-medium text-slate-100">
            <td className="px-3 py-2">Total</td>
            <td className="px-3 py-2 text-right tabular-nums">{total}</td>
            <td className="px-3 py-2 text-right tabular-nums">100%</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function DeckAnalyticsModal({ deckName, lines, onClose }) {
  const deckSpheres = useMemo(() => spheresInDeck(lines), [lines]);
  const [selectedSphere, setSelectedSphere] = useState(null);
  const curve = useMemo(
    () => computeCostCurve(lines, { sphere: selectedSphere }),
    [lines, selectedSphere]
  );
  const curveLabel =
    selectedSphere == null ? "All spheres" : selectedSphere === "—" ? "No sphere" : selectedSphere;
  const barClass =
    selectedSphere == null ? "bg-amber-500/80" : sphereAccentClasses(selectedSphere).chartBar;

  const reducedMotion = prefersReducedMotion();
  const [open, setOpen] = useState(reducedMotion);
  const [barsRevealed, setBarsRevealed] = useState(reducedMotion);
  const [isClosing, setIsClosing] = useState(false);
  const panelRef = useRef(null);
  const closeFinishedRef = useRef(false);
  const closeFallbackRef = useRef(null);
  const prevCurveKeyRef = useRef(null);
  const curveKey = selectedSphere ?? "__all__";

  useEffect(() => {
    if (selectedSphere == null) return;
    if (!deckSpheres.some((s) => s.key === selectedSphere)) setSelectedSphere(null);
  }, [deckSpheres, selectedSphere]);

  useEffect(() => {
    if (prevCurveKeyRef.current === null) {
      prevCurveKeyRef.current = curveKey;
      return;
    }
    if (prevCurveKeyRef.current === curveKey) return;
    prevCurveKeyRef.current = curveKey;
    if (reducedMotion) return;
    setBarsRevealed(false);
    let innerRaf = 0;
    const outerRaf = requestAnimationFrame(() => {
      innerRaf = requestAnimationFrame(() => setBarsRevealed(true));
    });
    return () => {
      cancelAnimationFrame(outerRaf);
      if (innerRaf) cancelAnimationFrame(innerRaf);
    };
  }, [curveKey, reducedMotion]);

  const finishClose = useCallback(() => {
    if (closeFinishedRef.current) return;
    closeFinishedRef.current = true;
    if (closeFallbackRef.current != null) {
      window.clearTimeout(closeFallbackRef.current);
      closeFallbackRef.current = null;
    }
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (reducedMotion) return;
    const raf = requestAnimationFrame(() => setOpen(true));
    const barTimer = window.setTimeout(() => setBarsRevealed(true), 90);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(barTimer);
      if (closeFallbackRef.current != null) window.clearTimeout(closeFallbackRef.current);
    };
  }, [reducedMotion]);

  const dismiss = useCallback(() => {
    if (isClosing || closeFinishedRef.current) return;
    if (reducedMotion) {
      onClose();
      return;
    }
    setIsClosing(true);
    setBarsRevealed(false);
    // Two frames so the browser keeps the "open" computed styles before we animate closed.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setOpen(false);
      });
    });
    closeFallbackRef.current = window.setTimeout(finishClose, ANIM_MS + 80);
  }, [finishClose, isClosing, onClose, reducedMotion]);

  const handlePanelTransitionEnd = useCallback(
    (e) => {
      if (!isClosing || e.target !== panelRef.current) return;
      if (e.propertyName !== "transform") return;
      finishClose();
    },
    [finishClose, isClosing]
  );

  const animStyle = { transitionDuration: `${ANIM_MS}ms`, transitionTimingFunction: EASE };

  return (
    <div
      className={[
        "fixed inset-0 z-50 flex items-end justify-center p-0 backdrop-blur-sm transition-opacity sm:items-center sm:p-4",
        open ? "bg-black/70" : "bg-black/0",
        isClosing ? "pointer-events-none" : "",
      ].join(" ")}
      style={animStyle}
      role="dialog"
      aria-modal="true"
      aria-labelledby="deck-analytics-title"
      onClick={dismiss}
      onKeyDown={(e) => e.key === "Escape" && dismiss()}
    >
      <div
        ref={panelRef}
        className={[
          "max-h-[min(90vh,100%)] w-full max-w-lg overflow-y-auto rounded-t-xl border border-slate-600 bg-slate-900 shadow-2xl transition-[transform,opacity] sm:rounded-xl",
          open
            ? "translate-y-0 opacity-100 sm:scale-100"
            : "translate-y-full opacity-0 sm:translate-y-3 sm:scale-[0.96] sm:opacity-0",
        ].join(" ")}
        style={animStyle}
        onTransitionEnd={handlePanelTransitionEnd}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-slate-700 bg-slate-900/95 px-4 py-4 backdrop-blur sm:px-6">
          <div className="min-w-0">
            <h2 id="deck-analytics-title" className="text-lg font-bold text-slate-50">
              Analytics
            </h2>
            {deckName ? (
              <p className="truncate text-sm text-slate-400">{deckName}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={dismiss}
            className="shrink-0 rounded-lg border border-slate-600 px-3 py-1 text-sm text-slate-300 hover:bg-slate-800"
          >
            Close
          </button>
        </div>

        <div
          className={[
            "space-y-4 px-4 py-5 transition-opacity sm:px-6",
            open ? "opacity-100" : "opacity-0",
          ].join(" ")}
          style={{ ...animStyle, transitionDuration: `${Math.round(ANIM_MS * 0.65)}ms` }}
        >
          <section aria-labelledby="cost-curve-heading">
            <h3
              id="cost-curve-heading"
              className="text-sm font-semibold uppercase tracking-wide text-amber-200/90"
            >
              Cost curve
            </h3>
            <p className="mt-1 text-xs text-slate-500">
              How many cards (copies included) sit at each printed cost. Heroes are not counted. Pick a
              sphere to see that slice of the deck.
            </p>
            <CostCurveSphereFilter
              spheres={deckSpheres}
              selectedSphere={selectedSphere}
              onSelectSphere={setSelectedSphere}
            />
            <p className="mt-3 text-xs text-slate-400">
              Showing <span className="font-medium text-slate-200">{curveLabel}</span>
              {curve.total > 0 ? (
                <>
                  {" "}
                  · <span className="tabular-nums">{curve.total}</span>{" "}
                  {curve.total === 1 ? "card" : "cards"}
                </>
              ) : null}
            </p>
            <div className="mt-4">
              <CostCurveChart
                buckets={curve.buckets}
                total={curve.total}
                maxCount={curve.maxCount}
                barsRevealed={barsRevealed}
                barClass={barClass}
                sphereLabel={curveLabel}
              />
              <CostCurveTable buckets={curve.buckets} total={curve.total} />
            </div>
          </section>
        </div>

        <div className="h-[max(0.75rem,env(safe-area-inset-bottom))] sm:hidden" />
      </div>
    </div>
  );
}
